/* eslint complexity: "off", camelcase: "off" */
import React, { Fragment } from 'react'
import styled from 'styled-components'
import { graphql, compose } from 'react-apollo'
import PropTypes from 'prop-types'
import Cell from './Cell'
import Tooltip from '../common/Tooltip'
import CreateCashflowDatum from './queries/CreateCashflowDatum'
import DestroyCashflowData from './queries/DestroyCashflowData'
import fillNullCashflowData from './helpers/fillNullCashflowData'
import processInput from './helpers/processInput'
import findDiscrepancies from './helpers/findDiscrepancies'
import formatNumber from './helpers/formatNumber'
import getMostRecentData from './helpers/getMostRecentData'
import DebtAdjustment from './DebtAdjustment'
import cashflowNotes from './configs/cashflow_notes.json'

const Container = styled.div`
  display: grid;
  grid-template-columns: ${props => props.isPersonalDebt ? '2fr 5fr' : '2fr repeat(5, 1fr)'};
  padding: 10px 10px 10px 0px;
  background: ${props => props.isHovered ? 'white' : '#EEEEEE'};
`
const Title = styled.div`
  text-align: right;
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`
const EditIcon = styled.span`
  color: white;
  background: black;
  border-radius: 100%;
  margin-left: 3px;
  padding: 2px;
  cursor: pointer;
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  position: absolute;
  right: -20px;
  background: #dd6a01;
  top: -5px;
  font-size: 80%;
`
const DeleteIcon = EditIcon.extend`
  bottom: -5px;
  top: initial;
`
const SubContainer = styled.div`
  grid-column-start: 2;
  grid-column-end: 7;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  margin-top: 15px;
`
const Reason = styled.textarea`
  grid-column-start: 1;
  grid-column-end: 5;
  width: 100%;
  width: 93.5%;
  padding: 4px 6px;
  justify-self: end;
  align-self: end;
  resize: none;
  margin: 10px 0px 0px 0px;
`
const Button = styled.button`
  color: white;
  background: #dd6a01;
  padding: 2px 0px;
  width: 75%;
  border-radius: 3px;
  border: none;
  &:disabled {
    background: #dddddd;
    cursor: default;
  }
`
const Controls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  grid-column-start: 5;
  grid-column-end: 5;
  text-align: center;
  margin-top: 8px;
`
const DiscrepancyControls = styled.div`
  grid-column-start: 2;
  grid-column-end: 6;
  display: grid;
  grid-template-columns: 4fr 1fr 4fr;
`
const Keep = Button.extend`
  grid-column-start: 1;
  grid-column-end: 1;
  justify-self: end;
  width: 60%;
`
const DoNotKeep = Button.extend`
  background: #dbdbdc;
  grid-column-start: 3;
  grid-column-end: 3;
  justify-self: start;
  width: 60%;
`
const Cancel = styled.a`
  width: 75%;
  text-align: left;
  cursor: pointer;
`
const Corrections = styled.div`
  display: grid;
  grid-template-columns: 2fr repeat(5, 1fr);
  grid-column-start: 1;
  grid-column-end: 7;
  justify-self: end;
  width: 100%;
  margin: 5px 0px;
  background: #fef58b;
`
const CorrectionText = styled.div`
  grid-column-start: 1;
  grid-column-end: 3;
  white-space: nowrap;
`
const Value = styled.div`
  text-align: right;
`
const HelperLine = styled.div`
  width: calc(100% + 2px);
  background-color: #60B8C7;
  color: white;
  text-align: center;
  height: 45px;
  line-height: 45px;
`
export class Row extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      inlineEdit: false,
      isHovered: false,
      isIconHovered: false,
      isDeleteIconHovered: false,
      isButtonDisabled: true,
      doNotKeep: false, // flag to determine if the user decided not to keep adjustments with tax transcripts
      hasDiscrepancy: this.getOriginalDiscrepancyData(props.data).size > 0, // are there any discrepancies in the row
      reason: this.getOriginalReason(props.data),
      isPersonalDebt: props.segmentKey === 'personal_debt'
    }
    this.data = new Map() // container for inline adjustments [key: year, value: data]
  }
  getOriginalReason = data => {
    for (const datum of data) {
      if (datum.reason) {
        return datum.reason
      }
    }
  }
  static getDerivedStateFromProps(props, state) {
    return { hasDiscrepancy: findDiscrepancies(props.data) > 0, isEditable: props.isEditable ? true : false }
  }
  shouldComponentUpdate(nextProps) {
    return nextProps.isEditable !== this.props.isEditable || nextProps.resourceType === this.props.resourceType
  }
  _createValue = async (data, year) => {
    const { entityId, resourceType, createCashflowDatum, category, fiscalYears } = this.props
    const { reason } = this.state
    const fiscalYear = fiscalYears.slice().find(fy => fy.year === year)
    return await createCashflowDatum({
      variables: {
        input: {
          cashflowDatumFields: {
            year,
            resourceId: entityId,
            resourceType,
            category: category.key,
            data,
            reason,
            source: 'keyed'
          }
        }
      }
    })
  }
  onMouseEnter = () => {
    this.setState({ isHovered: true })
  }
  onMouseLeave = () => {
    this.setState({ isHovered: false })
  }
  onIconMouseOver = () => {
    this.setState({ isIconHovered: true })
  }
  onIconMouseOut = () => {
    this.setState({ isIconHovered: false })
  }
  onDeleteIconMouseOver = () => {
    this.setState({ isDeleteIconHovered: true })
  }
  onDeleteIconMouseOut = () => {
    this.setState({ isDeleteIconHovered: false })
  }
  onReasonChange = (e) => {
    this.setState({
      reason: e.target.value
    }, this.validate)
  }
  enableEdit = () => {
    this.setState({
      inlineEdit: true
    })
  }
  disableEdit = () => {
    const { data } = this.props
    let hasDiscrepancy = false
    if (findDiscrepancies(data).length > 0) {
      hasDiscrepancy = true
    }
    this.setState({
      inlineEdit: false,
      doNotKeep: false,
      reason: this.getOriginalReason(data),
      isButtonDisabled: true,
      hasDiscrepancy
    })
    this.data = new Map()
  }
  validate = () => {
    const { category, data, years } = this.props
    const { reason } = this.state
    let isButtonDisabled = true
    const values = Array.from(this.data.values())
    const mostRecentData = fillNullCashflowData(data, years, category.key)
    const areNegativesAllowed = category.key === 'other_business_cashflow'
    // Check if values are negative, or if category is `other_business_cashflow` positive
    // and negative values are allowed
    const areValuesValid = areNegativesAllowed ? true : !values.some(d => d < 0)
    const isThereData = values.some(d => typeof d === 'number')
    const isReasonValid = reason && reason.length > 0
    const isValidNewAdjustment = areValuesValid && isThereData && isReasonValid
    const isReasonUpdate = mostRecentData.some(d => d.source === 'keyed') && isReasonValid
    if (isValidNewAdjustment || isReasonUpdate) {
      isButtonDisabled = false
    }
    this.setState({ isButtonDisabled })
  }
  save = () => {
    const { refetch, years, data, fiscalYears } = this.props
    const mutations = []
    this.setState({
      isButtonDisabled: true
    })
    for (const year of Array.from(years.keys())) {
      const fiscalYear = fiscalYears.find(y => y.year === year)
      if (this.data.has(year)) {
        mutations.push(this._createValue(this.data.get(year), year, fiscalYear.days))
      } else if (data.find(datum => datum.year === year)) {
        const datum = getMostRecentData(year, data).find(datum => datum.year === year && datum.source !== 'derived')
        if (datum) {
          mutations.push(this._createValue(datum.data, year))
        }
      }
    }
    Promise.all(mutations).then(() => {
      this.setState({ inlineEdit: false, isButtonDisabled: true })
      this.data = new Map()
      refetch()
    }).catch(err => {
      console.log(err)
    })
  }
  keep = () => {
    const { refetch, data } = this.props
    const originalDiscrepancyData = this.getOriginalDiscrepancyData(data)
    const mutations = data.map(async datum => {
      if (datum.source === 'tax_transcript' && originalDiscrepancyData.has(datum.year)) {
        return await this._createValue(datum.data, datum.year)
      }
    })
    Promise.all(mutations).then(() => {
      this.disableEdit()
      refetch()
    }).catch(err => {
      console.log(err)
    })
  }
  doNotKeep = () => {
    const { data, years } = this.props
    const oldData  = this.getOriginalDiscrepancyData(data)
    for (const year of Array.from(years.keys())) {
      if (oldData.has(year)) {
        this.data.set(year, oldData.get(year).data)
      }
    }
    this.setState({ hasDiscrepancy: false, doNotKeep: true, reason: '' })
    this.validate()
  }
  deleteAdjustments = async () => {
    const { category, createCashflowDatum, destroyCashflowData, entityId, resourceType, refetch, years, fiscalYears } = this.props
    const { cashflowData } = this.props.entity
    const mutations = Array.from(years.keys()).map(year => {
      const fiscalYear = fiscalYears.find(y => y.year === year)
      const dataForYearAndCat = cashflowData.filter(datum => datum.year === year && datum.category === category.key)
      if (dataForYearAndCat.length > 0) {
        const taxDatum =       dataForYearAndCat.find(datum => datum.source === 'tax_transcript')
        const manualDatum =    dataForYearAndCat.find(datum => datum.source === 'manual_transcript')
        const extractedDatum = dataForYearAndCat.find(datum => datum.source === 'extracted_document')
        const documentDatum =  dataForYearAndCat.find(datum => datum.source === 'manual_document')
        const keyedDatum =     dataForYearAndCat.find(datum => datum.source === 'keyed')
        let datum = null
        if (!category.required && !category.derived) {
          if (keyedDatum) {
            datum = { ...keyedDatum, data: 0 } // Zero out additional categories as they did not exist
          } else {
            datum = { data: 0, days: null }
          }
        } else if (!category.required && !category.derived && !keyedDatum) {
          datum = null
        } else if (taxDatum) {
          datum = taxDatum
        } else if (manualDatum) {
          datum = manualDatum
        } else if (extractedDatum) {
          datum = extractedDatum
        } else if (documentDatum) {
          datum = documentDatum
        }
        if (fiscalYear.period === 'interim' && resourceType === 'Guarantor' && keyedDatum) {
          return destroyCashflowData({
            variables: {
              resourceType,
              resourceId: entityId,
              dataKeys: [
                {
                  year,
                  category: category.key,
                  source: 'keyed',
                  period: 'interim'
                }
              ]
            }
          })
        }
        if (datum !== null) {
          return createCashflowDatum({
            variables: {
              input: {
                cashflowDatumFields: {
                  year,
                  resourceId: entityId,
                  resourceType,
                  category: category.key,
                  data: datum.data,
                  reason: null,
                  source: 'keyed'
                }
              }
            }
          })
        }
      }
    })
    Promise.all(mutations).then(() => {
      this.data = new Map()
      refetch()
    }).catch(err => {
      console.log(err)
    })
  }
  getOriginalDiscrepancyData = data => {
    const discrepancies = findDiscrepancies(data)
    const oldData = new Map()
    const sortedData = data.sort((a, b) => b.createdAt - a.createdAt) // sort data newest to oldest to find the second most recent data
    for (const discrepancy of discrepancies) {
      if (!oldData.has(discrepancy.year)) {
        const oldDatum = sortedData.find(datum => datum.year === discrepancy.year && datum.source !== 'tax_transcript')
        oldData.set(discrepancy.year, oldDatum)
      }
    }
    return oldData
  }
  isCellEditable = datum => {
    // TODO: Clean this up
    const { inlineEdit, hasDiscrepancy } = this.state
    const { resourceType, years, isEditable, category, yearSources, fiscalYears } = this.props
    const isAverageYear = resourceType === 'Guarantor' && fiscalYears.find(fy => fy.year === datum.year).period === 'interim'
    const yearHasTranscripts = years.get(datum.year).hasTranscripts

    // temporary fix for guarantor cashflows
    if (isAverageYear && (inlineEdit || isEditable)) return true

    if (yearSources[datum.year]) {
      if (datum.data === null && !category.derived && !category.required && inlineEdit) {
        return true
      } else if (isEditable && !category.derived && !category.required) { // Additional categories are not editable in enter spreads
        return false
      } else if (!inlineEdit && isAverageYear && yearSources[datum.year].period !== 'tax') { // Averaged values are not editable in edit mode
        return false
      } else if (inlineEdit && datum.source === 'derived' && !category.derived && isAverageYear && yearSources[datum.year].period === 'interim') { // Average values are editable inline
        return true
      } else if (inlineEdit && datum.data === null && !category.derived && isAverageYear && yearSources[datum.year].period === 'interim') { // Nil cells in average years should be editable inline
        return true
      } else if (isEditable && datum.source === 'derived' && !category.derived && !isAverageYear && yearSources[datum.year].period === 'tax') { // Derived values are not editable
        return true
      } else if (datum.source === 'derived') { // derived data should not be editable
        return false
      } else if (datum.source === 'tax_transcript' && !inlineEdit) { // Tax transcripts are not editable in edit mode
        return false
      } else if (hasDiscrepancy && inlineEdit) { // Discrepancies are not (initially) editable in edit mode
        return false
      } else if (years.get(datum.year).hasTranscripts && isEditable) { // Any columns with tax transcripts should not be editable in edit mode
        return false
      } else if (inlineEdit && datum.data === null && !yearHasTranscripts) {
        return false
      } else if (category.derived) { // derived categories should not be editable
        return false
      } else if (isAverageYear && datum.source === 'keyed') {
        return true
      } else if (isAverageYear) {
        return false
      } else { // Defer to which type of editing is being done if none of the above conditions are met
        return isEditable || inlineEdit
      }
    }
  }
  renderCells = () => {
    const {
      category,
      entityId,
      cellRef,
      moveFns,
      changeCell,
      tabIndices,
      loanId,
      years,
      data,
      resourceType,
      refetch,
      entity,
      interimYears,
      yearSources,
      isEditable,
      markForDelete,
      saveFromDelete,
      interimPeriods,
      fiscalYears,
      cashflowOptions,
      queriedYears,
      loan,
      markForSave,
      dontSave
    } = this.props
    const { inlineEdit, reason, isPersonalDebt, doNotKeep } = this.state
    // Specify category key in fillNullCashflowData to pass correct category down to cells
    const discrepancies = findDiscrepancies(data)
    if (!isPersonalDebt || !inlineEdit) {
      const sorted = data.sort((a, b) => b.createdAt - a.createdAt)
      return fillNullCashflowData(data, years, category.key).map((datum, i) => {
        const { days, period, annualizationCoefficient } = fiscalYears.find(y => y.year === datum.year)
        const discrepancy = Boolean(discrepancies.find(discrepancy => discrepancy.year === datum.year))
        const yearSource = yearSources[datum.year]
        let dataToRender = null
        if (discrepancy && !doNotKeep) {
          dataToRender = datum.data
        } else if (doNotKeep && sorted.some(d => d.year === datum.year && d.source !== 'tax_transcript')) {
          dataToRender = sorted.find(d => d.year === datum.year && d.source !== 'tax_transcript').data
        } else if (!category.required && !category.derived && datum.data === 0) {
          dataToRender = null
        } else {
          dataToRender = datum.data
        }
        return (
          <Cell
            mutateOnBlur={!inlineEdit}
            isEditable={this.isCellEditable(datum)}
            key={`${datum.year}-${datum.category}`}
            data={dataToRender}
            category={category.key}
            categoryInfo={category}
            entityId={entityId}
            resourceType={resourceType}
            year={parseInt(datum.year, 10)}
            cellRef={cellRef}
            moveFns={moveFns}
            changeCell={changeCell}
            tabIndex={tabIndices[`${datum.year}-${category.key}`]}
            loanId={loanId}
            refetch={refetch}
            source={datum.source}
            onChange={inlineEdit ? e => this.onCellChange(e, datum.year) : () => {}}
            discrepancy={discrepancy}
            originalReason={reason}
            entity={entity}
            isItalic={this.hasCellBeenEdited(datum)}
            interimYears={interimYears}
            years={years}
            yearSource={yearSource ? yearSource.source : null}
            yearPeriod={yearSource ? yearSource.period : null}
            inlineEdit={inlineEdit}
            markForDelete={markForDelete}
            saveFromDelete={saveFromDelete}
            isTableEditable={isEditable}
            id={`${category.key}_${i}`}
            period={period}
            days={days}
            cashflowOptions={cashflowOptions}
            loan={loan}
            queriedYears={queriedYears}
            annualizationCoefficient={annualizationCoefficient}
            markForSave={markForSave}
            dontSave={dontSave}
          />
        )
      })
    } else if (inlineEdit && isPersonalDebt) {
      return (
        <DebtAdjustment
          years={years}
          entityId={entityId}
          refetch={refetch}
          onCancel={this.disableEdit}
          category={category.key}
          data={data}
          inlineEdit
          isEditable
        />
      )
    }
  }
  renderTooltip = () => {
    const { isIconHovered, hasDiscrepancy, isDeleteIconHovered } = this.state
    if (this.icon && isIconHovered) {
      return (
        <Tooltip bottomEdge={this.container.getBoundingClientRect().height - 10}>
          {!this.hasRowBeenEdited() || hasDiscrepancy ? 'Add Adjustments' : 'Edit Adjustments'}
        </Tooltip>
      )
    } else if (this.deleteIcon && isDeleteIconHovered) {
      return (
        <Tooltip bottomEdge={this.container.getBoundingClientRect().height - 10 - this.deleteIcon.getBoundingClientRect().height}>
          Delete Adjustments
        </Tooltip>
      )
    }
  }
  renderInlineControls = () => {
    const { category } = this.props
    const { inlineEdit, isButtonDisabled, hasDiscrepancy, reason, isPersonalDebt } = this.state
    if (inlineEdit && hasDiscrepancy && !isPersonalDebt) {
      return (
        <DiscrepancyControls>
          <CorrectionText>
            Would you like to keep the original values entered in the FRT?
          </CorrectionText>
          <Keep id={`${category.key}_keep`} onClick={this.doNotKeep}>Keep</Keep>
          <DoNotKeep id={`${category.key}_do_not_keep`} onClick={this.keep}>Do Not Keep</DoNotKeep>
        </DiscrepancyControls>
      )
    } else if (inlineEdit && !hasDiscrepancy && !isPersonalDebt) {
      return (
        <SubContainer>
          <Reason
            id={`${category.key}_reason`}
            value={reason}
            onChange={this.onReasonChange}
            placeholder="Provide a reason here why the amount needs to be changed and we will create the adjustment once it's saved."
          />
          <Controls>
            <Button id={`${category.key}_save`} disabled={isButtonDisabled} onClick={this.save}>Save</Button>
            <Cancel id={`${category.key}_cancel`} onClick={this.disableEdit}>Cancel</Cancel>
          </Controls>
        </SubContainer>
      )
    }
  }
  hasRowBeenEdited = () => {
    const { years, data } = this.props
    let mostRecentData = []
    for (const year of Array.from(years.keys())) {
      const dataForYear = data.filter(d => d.year === year)
      const dataToCheck = dataForYear.filter(datum => !dataForYear.some(d => d.data === datum.data && d.source !== datum.source)) // see if any adjustments have been deleted
      mostRecentData = mostRecentData.concat(getMostRecentData(year, dataToCheck))
    }
    return mostRecentData.some(datum => datum.source === 'keyed')
  }
  hasCellBeenEdited = (datum) => {
    const { data, category, resourceType } = this.props
    const filteredByYear = data.filter(d => d.year === datum.year)
    if (!category.derived && !category.required) {
      return true
    }
    if (filteredByYear.length > 1 && datum.source === 'keyed') {
      return !filteredByYear.some(d => d.data === datum.data && d.source !== datum.source)
    }
    if (datum.period === 'interim' && resourceType === 'Guarantor' && datum.source !== 'derived') {
      return true
    }
    return false
  }
  shouldRowRender = () => {
    const { data, years, category } = this.props
    const paddedData = fillNullCashflowData(data, years, category)
    if (!category.required && !category.derived && paddedData.every(datum => datum.data === 0 || datum.data === null)) { // additional category that has been zeroed out
      return false
    }
    return true
  }
  isEditIconVisible = () => {
    const { data, isEditable, years } = this.props
    const { isHovered, inlineEdit } = this.state
    // Show edit icon if at least one datum is not null or if at least one column has tax transcripts
    if ((data.some(datum => datum.data !== null) || Array.from(years.values()).some(year => year.hasTranscripts)) && !inlineEdit && !isEditable && isHovered) {
      return true
    }
    return false
  }
  onCellChange = (e, year) => {
    const data = processInput(e.target.value)
    this.data.set(year, data)
    this.validate()
  }
  renderDiscrepancyCorrections = () => {
    const { data, years, category } = this.props
    const { inlineEdit, hasDiscrepancy } = this.state
    const originalDiscrepancyData = this.getOriginalDiscrepancyData(data)
    if (hasDiscrepancy && inlineEdit) {
      return (
        <Corrections>
          <Title>
            Entered by Admin
          </Title>
          {
            fillNullCashflowData(Array.from(originalDiscrepancyData.values()), years, category.key).map(datum => {
              return <Value key={`${datum.category}-discrepancy-${datum.year}`}>{formatNumber(datum.data)}</Value>
            })
          }
        </Corrections>
      )
    }
  }
  renderHelperLine = () => {
    const { isEditable, category, selectedYear, selectedCategory, years } = this.props
    if (isEditable) {
      if (category.key === selectedCategory) {
        if (selectedYear && selectedCategory && years.has(selectedYear)) {
          const { taxReturn, period } = years.get(selectedYear)
          if (taxReturn && period !== 'interim' && cashflowNotes[taxReturn.formName][selectedCategory]) {
            return (
              <HelperLine>
                {cashflowNotes[taxReturn.formName][selectedCategory]}
              </HelperLine>
            )
          }
        }
      }
    }
  }
  renderEditButtons = () => {
    const { category, isEditAllowed, isEditable } = this.props
    const { isHovered, inlineEdit } = this.state
    if (!category.derived && isEditAllowed) {
      return (
        <Fragment>
          <EditIcon
            innerRef={node => this.icon = node}
            visible={this.isEditIconVisible()}
            onClick={this.enableEdit}
            className={!this.hasRowBeenEdited() ? 'fa fa-wrench' : 'fa fa-pencil'}
            onMouseOver={this.onIconMouseOver}
            onMouseOut={this.onIconMouseOut}
            id={`${category.key}_edit`}
          />
          <DeleteIcon
            innerRef={node => this.deleteIcon = node}
            visible={!isEditable && !inlineEdit && isHovered && this.hasRowBeenEdited()}
            onClick={this.deleteAdjustments}
            className="fa fa-trash"
            onMouseOver={this.onDeleteIconMouseOver}
            onMouseOut={this.onDeleteIconMouseOut}
            id={`${category.key}_delete`}
          />
        </Fragment>
      )
    }
  }
  render() {
    const { isEditAllowed, category, isEditable } = this.props
    const { isHovered, inlineEdit, isPersonalDebt } = this.state
    if (this.shouldRowRender()) {
      return (
        <Fragment>
          <Container
            isHovered={isHovered && !isEditable && !inlineEdit && !category.derived || inlineEdit}
            innerRef={node => this.container = node}
            onMouseEnter={this.onMouseEnter}
            onMouseLeave={this.onMouseLeave}
            isPersonalDebt={isPersonalDebt && inlineEdit}
          >
            <Title>
              {this.props.title}
              {this.renderEditButtons()}
              {isEditAllowed && !category.derived && this.renderTooltip()}
            </Title>
            {this.renderCells()}
            {this.renderDiscrepancyCorrections()}
            {this.renderInlineControls()}
          </Container>
          {this.renderHelperLine()}
        </Fragment>
      )
    } else {
      return null
    }
  }
}

Row.propTypes = {
  category: PropTypes.object.isRequired,
  cellRef: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  entityId: PropTypes.number,
  isEditable: PropTypes.bool
}

export default compose(
  graphql(CreateCashflowDatum, {
    name: 'createCashflowDatum'
  }),
  graphql(DestroyCashflowData, {
    name: 'destroyCashflowData'
  })
)(Row)
